import { useCallback, useState } from "react";

import { addDays, startOfDay } from "date-fns";

import { NamedDateRange } from "components/date-range-filter/utils";
import { FaxesQueryParams } from "services/types/fax.type";

/** Which date range a tab reads. Blocked shares All Faxes' range, as it always has. */
export type FaxDateScope = "standard" | "queue" | "archived";

export type FaxTabDateRange = {
  after_date: string | null;
  before_date: string | null;
  /** The picker's preset ("today", "week", …), or "" for typed dates or no range at all. */
  preset: NamedDateRange;
};

export const getFaxDateScope = (
  filters: FaxesQueryParams,
  isUnassignedQueueEnabled: boolean
): FaxDateScope => {
  if (!isUnassignedQueueEnabled || filters.blocked) return "standard";
  if (filters.unassigned) return "queue";
  if (filters.deactivated) return "archived";
  return "standard";
};

// The same ISO strings the picker reports for Today, so its first report is not a change.
const todayRange = (): FaxTabDateRange => {
  const today = startOfDay(new Date());
  return {
    after_date: today.toISOString(),
    before_date: addDays(today, 1).toISOString(),
    preset: "today"
  };
};

const NO_RANGE: FaxTabDateRange = {
  after_date: null,
  before_date: null,
  preset: ""
};

/**
 * One date range per Fax Center tab (ENG-2511). All Faxes and Blocked keep today's Today default;
 * Unassigned and Archived start with no range, so a fax nobody has handled, or one archived today
 * but received last week, is never hidden by it. Each tab keeps what the user picked while they
 * look at another one.
 *
 * Flag off there is only the standard range and it starts empty, exactly as before: the picker
 * sets Today on mount. Flag on it starts at Today, so opening All Faxes does not first fetch every
 * fax with no range at all.
 */
export default function useFaxTabDateRanges(isUnassignedQueueEnabled: boolean) {
  const [ranges, setRanges] = useState<Record<FaxDateScope, FaxTabDateRange>>(
    () => ({
      standard: isUnassignedQueueEnabled
        ? todayRange()
        : { ...NO_RANGE, preset: "today" },
      queue: NO_RANGE,
      archived: NO_RANGE
    })
  );

  const updateRange = useCallback(
    (scope: FaxDateScope, range: FaxTabDateRange) =>
      setRanges((previous) => ({ ...previous, [scope]: range })),
    []
  );

  return { ranges, updateRange };
}
